import { sendMessageToThread, createThread } from './openai';
import { serverTimestamp } from 'firebase/firestore';
import { PetProfile, OnboardingStep, ImageAnalysisResult } from '../types/onboarding';

export interface ConversationContext {
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
  currentStep: OnboardingStep;
  petData: Partial<PetProfile>;
  analysisResult?: ImageAnalysisResult;
  threadId?: string;
}

export interface AIResponse {
  message: string;
  suggestedActions?: string[];
  extractedData?: Partial<PetProfile>;
  nextStep?: OnboardingStep;
}

/**
 * Initializes a new onboarding conversation thread
 * @param userLanguage - User's preferred language
 * @returns Promise<string> - Thread ID for the conversation
 */
export const initializeOnboardingConversation = async (
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en'
): Promise<string> => {
  try {
    const threadId = await createThread();

    // Send initial welcome message
    const welcomeMessage = getWelcomeMessage(userLanguage);
    await sendMessageToThread(threadId, welcomeMessage);

    return threadId;
  } catch (error) {
    console.error('Error initializing onboarding conversation:', error);
    throw new Error('Failed to start onboarding conversation');
  }
};

/**
 * Processes user input during onboarding and returns AI response
 * @param userMessage - User's message
 * @param context - Current conversation context
 * @returns Promise<AIResponse> - AI response with extracted data and next steps
 */
export const processOnboardingMessage = async (
  userMessage: string,
  context: ConversationContext
): Promise<AIResponse> => {
  try {
    if (!context.threadId) {
      throw new Error('No conversation thread available');
    }

    // Create context-aware prompt for the AI
    const systemPrompt = createOnboardingPrompt(context);
    const fullPrompt = `${systemPrompt}\n\nUser message: ${userMessage}`;

    // Send message to AI
    const aiResponse = await sendMessageToThread(context.threadId, fullPrompt);

    // Parse and structure the AI response
    return parseAIResponse(aiResponse, context);

  } catch (error) {
    console.error('Error processing onboarding message:', error);
    return {
      message: getErrorMessage(context.userLanguage),
      suggestedActions: [getRetryAction(context.userLanguage)]
    };
  }
};

/**
 * Handles photo upload and analysis workflow
 * @param imageUrl - URL of uploaded pet photo
 * @param petType - Type of pet ('dog' or 'cat')
 * @param context - Current conversation context
 * @returns Promise<AIResponse> - Response with analysis results
 */
export const processPhotoAnalysis = async (
  imageUrl: string,
  petType: 'dog' | 'cat',
  context: ConversationContext
): Promise<AIResponse> => {
  try {
    // Import the analysis function here to avoid circular dependencies
    const { analyzePetImage } = await import('./petImageAnalysis');

    // Analyze the image
    const analysisResult = await analyzePetImage(imageUrl, petType, context.userLanguage);

    // Create enthusiastic response based on analysis
    const responseMessage = createAnalysisResponse(analysisResult, context.userLanguage);

    return {
      message: responseMessage,
      extractedData: {
        type: petType,
        breed: analysisResult.breed,
        age: analysisResult.age,
        gender: analysisResult.sex,
        aiAnalysisData: {
          breedConfidence: analysisResult.confidence,
        ageConfidence: analysisResult.ageConfidence,
        sexConfidence: analysisResult.sexConfidence,
        analysisDate: serverTimestamp() as any,
        analysisImageUrl: analysisResult.analysisImageUrl
        }
      },
      nextStep: OnboardingStep.CONFIRM_DETAILS
    };

  } catch (error) {
    console.error('Error processing photo analysis:', error);
    return {
      message: getAnalysisErrorMessage(context.userLanguage),
      suggestedActions: [
        getRetryAnalysisAction(context.userLanguage),
        getSkipAnalysisAction(context.userLanguage)
      ]
    };
  }
};

/**
 * Processes pet name input and completes onboarding
 * @param petName - Name provided by user
 * @param context - Current conversation context
 * @returns Promise<AIResponse> - Completion response
 */
export const processPetName = async (
  petName: string,
  context: ConversationContext
): Promise<AIResponse> => {
  try {
    // Validate pet name
    const validatedName = validatePetName(petName);

    const completionMessage = getCompletionMessage(validatedName, context.userLanguage);

    return {
      message: completionMessage,
      extractedData: {
        name: validatedName,
        onboardingCompleted: true,
        onboardingStep: OnboardingStep.COMPLETED
      },
      nextStep: OnboardingStep.COMPLETED,
      suggestedActions: [
        getViewProfileAction(context.userLanguage),
        getAddAnotherPetAction(context.userLanguage)
      ]
    };

  } catch (error) {
    return {
      message: getNameValidationError(context.userLanguage),
      suggestedActions: [getRetryNameAction(context.userLanguage)]
    };
  }
};

// Helper functions for creating language-specific content

const getWelcomeMessage = (language: string): string => {
  const messages = {
    en: "Welcome to PetSnap! I'm excited to help you create a personalized profile for your furry friend. To get started, could you please upload a photo of your pet? This will help me provide better care recommendations.",
    'zh-CN': "欢迎使用PetSnap！我很高兴帮助您为您的毛茸茸的朋友创建一个个性化资料。要开始，请您上传宠物的照片？这将帮助我提供更好的护理建议。",
    es: "¡Bienvenido a PetSnap! Estoy emocionado de ayudarte a crear un perfil personalizado para tu amigo peludo. Para comenzar, ¿podrías subir una foto de tu mascota? Esto me ayudará a proporcionar mejores recomendaciones de cuidado.",
    fr: "Bienvenue sur PetSnap ! Je suis ravi de vous aider à créer un profil personnalisé pour votre ami à fourrure. Pour commencer, pourriez-vous télécharger une photo de votre animal ? Cela m'aidera à fournir de meilleures recommandations de soins."
  };
  return (messages as any)[language] || messages.en;
};

const createOnboardingPrompt = (context: ConversationContext): string => {
  const basePrompt = `You are a friendly pet care assistant helping a user create their pet's profile. Current step: ${context.currentStep}

Current pet information: ${JSON.stringify(context.petData, null, 2)}

Guidelines:
- Be enthusiastic and friendly
- Ask one question at a time
- Extract relevant information from user responses
- Validate information when possible
- Respond in the user's language: ${context.userLanguage}
- Keep responses concise but helpful
- If information is unclear, ask for clarification`;

  return basePrompt;
};

const createAnalysisResponse = (result: ImageAnalysisResult, language: string): string => {
  const templates = {
    en: `Wow! What a beautiful ${result.sex === 'male' ? 'boy' : 'girl'}! 🤩

Based on the photo, I estimate this is a ${result.breed} around ${result.age} years old. ${result.description}

Does this sound right to you? Feel free to correct any details!`,
    'zh-CN': `哇！多么漂亮的${result.sex === 'male' ? '男孩' : '女孩'}！🤩

根据照片，我估计这是一只大约${result.age}岁的${result.breed}。${result.description}

这听起来对吗？请随时纠正任何细节！`,
    es: `¡Wow! ¡Qué ${result.sex === 'male' ? 'chico' : 'chica'} tan hermosa! 🤩

Basándome en la foto, estimo que es un ${result.breed} de alrededor de ${result.age} años. ${result.description}

¿Suena correcto? ¡Siéntete libre de corregir cualquier detalle!`,
    fr: `Wow ! Quel ${result.sex === 'male' ? 'garçon' : 'fille'} magnifique ! 🤩

D'après la photo, j'estime que c'est un ${result.breed} d'environ ${result.age} ans. ${result.description}

Est-ce que cela vous semble correct ? N'hésitez pas à corriger les détails !`
  };

  return (templates as any)[language] || templates.en;
};

const parseAIResponse = (aiResponse: string, context: ConversationContext): AIResponse => {
  // This is a simplified parser - in a real implementation, you might want
  // to use more sophisticated parsing or structured outputs from the AI

  const response: AIResponse = {
    message: aiResponse
  };

  // Look for extracted data patterns in the response
  const nameMatch = aiResponse.match(/name[:\s]+([^\n.,!?]+)/i);
  if (nameMatch) {
    response.extractedData = { name: nameMatch[1].trim() };
  }

  return response;
};

const validatePetName = (name: string): string => {
  const trimmed = name.trim();

  if (trimmed.length < 1) {
    throw new Error('Name too short');
  }

  if (trimmed.length > 50) {
    throw new Error('Name too long');
  }

  // Basic sanitization - remove potentially harmful characters
  const sanitized = trimmed.replace(/[<>"'&]/g, "");

  return sanitized;
};

// Error and action messages
const getErrorMessage = (language: string): string => {
  const messages = {
    en: "I'm sorry, I encountered an error. Let's try that again.",
    'zh-CN': "抱歉，我遇到了一个错误。让我们再试一次。",
    es: "Lo siento, encontré un error. Intentemos eso de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur. Essayons encore."
  };
  return (messages as any)[language] || messages.en;
};

const getAnalysisErrorMessage = (language: string): string => {
  const messages = {
    en: "I had trouble analyzing that photo. Would you like to try uploading a different photo or provide the details manually?",
    'zh-CN': "我无法分析那张照片。您想尝试上传不同的照片还是手动提供详细信息？",
    es: "Tuve problemas analizando esa foto. ¿Te gustaría intentar subir una foto diferente o proporcionar los detalles manualmente?",
    fr: "J'ai eu du mal à analyser cette photo. Souhaitez-vous essayer de télécharger une photo différente ou fournir les détails manuellement ?"
  };
  return (messages as any)[language] || messages.en;
};

const getNameValidationError = (language: string): string => {
  const messages = {
    en: "That name doesn't look quite right. Could you please provide a valid name for your pet?",
    'zh-CN': "这个名字看起来不太对。请您为您的宠物提供一个有效的名字？",
    es: "Ese nombre no parece correcto. ¿Podrías proporcionar un nombre válido para tu mascota?",
    fr: "Ce nom ne semble pas correct. Pourriez-vous fournir un nom valide pour votre animal ?"
  };
  return (messages as any)[language] || messages.en;
};

const getCompletionMessage = (petName: string, language: string): string => {
  const messages = {
    en: `Perfect! Welcome to the family, ${petName}! 🎉 Your pet profile has been created successfully. You can now access personalized care recommendations and start your pet care journey.`,
    'zh-CN': `完美！欢迎加入家庭，${petName}！🎉 您的宠物资料已成功创建。现在您可以访问个性化的护理建议并开始您的宠物护理之旅。`,
    es: `¡Perfecto! Bienvenido a la familia, ${petName}! 🎉 Tu perfil de mascota ha sido creado exitosamente. Ahora puedes acceder a recomendaciones de cuidado personalizadas y comenzar tu viaje de cuidado de mascotas.`,
    fr: `Parfait ! Bienvenue dans la famille, ${petName} ! 🎉 Votre profil d'animal a été créé avec succès. Vous pouvez maintenant accéder à des recommandations de soins personnalisées et commencer votre voyage de soins pour animaux.`
  };
  return (messages as any)[language] || messages.en;
};

// Action buttons/messages
const getRetryAction = (language: string): string => {
  const actions = {
    en: "Try Again",
    'zh-CN': "重试",
    es: "Intentar de Nuevo",
    fr: "Réessayer"
  };
  return (actions as any)[language] || actions.en;
};

const getRetryAnalysisAction = (language: string): string => {
  const actions = {
    en: "Try Different Photo",
    'zh-CN': "尝试不同的照片",
    es: "Probar Foto Diferente",
    fr: "Essayer une Photo Différente"
  };
  return (actions as any)[language] || actions.en;
};

const getSkipAnalysisAction = (language: string): string => {
  const actions = {
    en: "Enter Details Manually",
    'zh-CN': "手动输入详细信息",
    es: "Ingresar Detalles Manualmente",
    fr: "Saisir les Détails Manuellement"
  };
  return (actions as any)[language] || actions.en;
};

const getRetryNameAction = (language: string): string => {
  const actions = {
    en: "Enter Pet Name",
    'zh-CN': "输入宠物名字",
    es: "Ingresar Nombre de Mascota",
    fr: "Saisir le Nom de l'Animal"
  };
  return (actions as any)[language] || actions.en;
};

const getViewProfileAction = (language: string): string => {
  const actions = {
    en: "View Pet Profile",
    'zh-CN': "查看宠物资料",
    es: "Ver Perfil de Mascota",
    fr: "Voir le Profil de l'Animal"
  };
  return (actions as any)[language] || actions.en;
};

const getAddAnotherPetAction = (language: string): string => {
  const actions = {
    en: "Add Another Pet",
    'zh-CN': "添加另一只宠物",
    es: "Agregar Otra Mascota",
    fr: "Ajouter un Autre Animal"
  };
  return (actions as any)[language] || actions.en;
};
